import React, { useEffect, useState, useContext } from 'react';
import { Card, ProgressBar, ListGroup } from 'react-bootstrap';
import { $authHost } from '../http/index';
import { Context } from '../index';
import { useApiCache } from '../hooks/useApiCache';
import { useDebounce } from '../hooks/useDebounce';

const UserDiscountInfo = () => {
    const { user } = useContext(Context);
    const [discountInfo, setDiscountInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const { getCachedData, setCachedData } = useApiCache();
    
    // Дебаунсим изменение состояния авторизации
    const debouncedIsAuth = useDebounce(user.isAuth, 300);

    useEffect(() => {
        const fetchDiscountInfo = async () => {
            // Не загружаем информацию о скидках, если пользователь не авторизован
            if (!debouncedIsAuth) {
                setLoading(false);
                return;
            }
            
            // Проверяем кэш
            const cacheKey = `discount-info-${user.user?.id || 'anonymous'}`;
            const cachedData = getCachedData(cacheKey);
            
            if (cachedData) {
                setDiscountInfo(cachedData);
                setLoading(false);
                return;
            }
            
            try {
                const { data } = await $authHost.get('/api/user/discount-info');
                setDiscountInfo(data);
                // Кэшируем данные на 2 минуты
                setCachedData(cacheKey, data, 2 * 60 * 1000);
            } catch (error) {
                console.error('Error fetching discount info:', error);
                // Если ошибка 429, не показываем ошибку пользователю
                if (error.response?.status !== 429) {
                    console.error('Error fetching discount info:', error);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchDiscountInfo();
    }, [debouncedIsAuth, user.user?.id, getCachedData, setCachedData]);

    if (loading) {
        return <Card className="mb-4"><Card.Body>Загрузка...</Card.Body></Card>;
    }

    if (!debouncedIsAuth) {
        return (
            <Card className="mb-4">
                <Card.Header as="h5">Информация о скидках</Card.Header>
                <Card.Body>
                    <div className="text-center text-muted">
                        Для просмотра информации о скидках необходимо войти в систему
                    </div>
                </Card.Body>
            </Card>
        );
    }

    if (!discountInfo) {
        return null;
    }

    const { currentDiscount, totalSpent, nextDiscount, discountLevels } = discountInfo;
    const progress = (totalSpent / nextDiscount.threshold) * 100;

    return (
        <Card className="mb-4">
            <Card.Header as="h5">Информация о скидках</Card.Header>
            <Card.Body>
                <div className="mb-4">
                    <h4>Ваша текущая скидка: {currentDiscount}%</h4>
                    <p className="text-muted">Общая сумма покупок: {totalSpent} ₽</p>
                </div>

                {nextDiscount.threshold > 0 && (
                    <div className="mb-4">
                        <p>До следующей скидки {nextDiscount.amount}% осталось:</p>
                        <ProgressBar 
                            now={Math.min(progress, 100)} 
                            animated 
                            label={`${Math.round(nextDiscount.threshold - totalSpent)} ₽`}
                        />
                    </div>
                )}

                <div>
                    <h5>Уровни скидок:</h5>
                    <ListGroup>
                        {discountLevels.map((item, index) => (
                            <ListGroup.Item 
                                key={index}
                                active={currentDiscount === item.discount}
                            >
                                {item.threshold} ₽ - {item.discount}% скидка
                                {currentDiscount === item.discount && ' (текущий уровень)'}
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                </div>
            </Card.Body>
        </Card>
    );
};

export default UserDiscountInfo; 